/* TODO: t() */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component, createContext } from 'react';
import ReactDOM from 'react-dom';
import { View, StyleSheet } from 'react-native';

import connectData, { ConnectedProps } from '../../support/connectData';
import formatCurrency from '../../core/support/formatCurrency';
import toElement from '../../support/helpers/toElement';

import { postJSON } from '../../support/Fetch';

import { APP_FIELDS } from '../../support/shared/constants/Constants';

import RenewalView from './FormEmbed/RenewalView';
import PaymentForm from './Payment/PaymentForm';
import { Alert, Button, Text } from '../uiold';
import { themeColors } from '../uiold/Constants';
import RawHTMLBlock from '../uiold/RawHTMLBlock';
import LoadingIndicator from '../uiold/LoadingIndicator';
import isInteractive from '../FormBuilder/FormElements/helpers/isInteractive';
import FieldGroup from '../FormBuilder/FieldGroupReadOnly';
import getFieldSets from '../FormBuilder/helpers/getFieldSets';
import getAccountCurrency from '../../support/helpers/getAccountCurrency';
import IndexedList from '../../support/shared/IndexedList';
import calculatePrice from '../../support/shared/calculatePrice';

import SignupFormModel from '../FormBuilder/SignupFormModel';
import { Group } from '../../types/shared/Group';
import { Field, MarketDates } from '../../types/shared/Vendor';
import { Market } from '../../types/shared/Market';
import { Tier } from '../../types/shared/Tier';
import { Addon } from '../../types/shared/Addon';
import isObject from '../../support/shared/isObject';
import getColorString from '../../support/helpers/getColorString';
import { getOptionFields } from '../../support/helpers/CategoryOptions';
import getAccountAchOnly from '../../support/helpers/getAccountAchOnly';

type FormSuccessProps = {
  success: boolean;
  id: string;
  price: number;
  currency: string;
  totalPrice: string;
};

type PreambleViewProps = {
  content: string;
  onAccept: (event?: any) => void;
  onViewLoaded?: () => void;
};

type FormEmbedProps = {
  accountID: string;
  formID: string;
  onViewLoaded?: () => void;
};

type FormEmbedState = {
  isPreambleAccepted: boolean;
  isRenewalDismissed: boolean;
  formSubmitSuccess: FormSuccessProps | null | undefined;
  renewalData: Array<Field> | null | undefined;
};

type SuccessViewProps = {
  content: string;
  onViewLoaded?: () => void;
  formDef: SignupFormModel;
  accountID: string;
  formSubmissionData: FormSuccessProps;
  paymentSettings: Exclude<FormEmbedFullProps['paymentSettings'], undefined>;
};

type FormViewProps = {
  accountID: string;
  formID: string;
  formDef: SignupFormModel;
  renewalData: Array<Field> | null | undefined;
  markets: IndexedList<Market>;
  priceTiers: IndexedList<Tier>;
  onSuccess: (data: FormSuccessProps) => void;
  onViewLoaded?: () => void;
  addons: Map<string, Addon> | null | undefined;
};

type FormViewState = {
  formError: string | null | undefined;
  isSubmitting: boolean;
  formData: Array<Field>;
  accountCurrency: string;
  validationErrors: Map<string, string | null | undefined>;
  serverValidationErrors: Map<string, string | null | undefined>;
  selectedMarketDates?: MarketDates;
  achOnly: boolean;
};

const EMPTY_GROUPS: Array<Group> = [];

type Context = {
  accountCurrency: string;
} | void;

export const FormContext = createContext<Context>(undefined);

class PreambleView extends Component<PreambleViewProps> {
  componentDidMount() {
    if (this.props.onViewLoaded) {
      this.props.onViewLoaded();
    }
  }

  render() {
    let { content, onAccept } = this.props;

    return (
      <View>
        <RawHTMLBlock content={content} />
        <View style={styles.centerButton}>
          <Button
            title="Proceed to Form"
            onPress={onAccept}
            style={styles.button}
            hoverStyle={styles.hoveredButton}
            textStyle={styles.buttonText}
          />
        </View>
      </View>
    );
  }
}

class FormView extends Component<FormViewProps, FormViewState> {
  state: FormViewState = {
    formData: [] as Array<Field>,
    formError: null,
    isSubmitting: false,
    accountCurrency: 'USD',
    validationErrors: new Map(),
    serverValidationErrors: new Map(),
    selectedMarketDates: {},
    achOnly: false,
  };

  componentDidMount() {
    let { onViewLoaded, renewalData } = this.props;
    if (onViewLoaded) {
      onViewLoaded();
    }
    this._fetchAccountCurrency(this.props.accountID);
    this._fetchAccountAchOnly(this.props.accountID);
    if (renewalData) {
      this._setRenewalFormData(renewalData);
    } else {
      // Note: we're keeping a copy of the formData in state because it's
      // necessary to render price and groups (hidden/shown). Ideally this should
      // be the source of truth, but it's actually just a mirror of what's in the
      // state of the individual (uncontrolled) form inputs.
      // TODO: Use controlled inputs.
      this.setState({
        formData: this._getFormData(),
      });
    }
  }

  render() {
    let { formDef } = this.props;
    return (
      <FormContext.Provider
        value={{ accountCurrency: this.state.accountCurrency }}
      >
        <View
          style={[
            styles.form,
            {
              backgroundColor: formDef?.backgroundColor
                ? getColorString(formDef.backgroundColor)
                : '',
            },
          ]}
        >
          {this._renderForm()}
        </View>
      </FormContext.Provider>
    );
  }

  _renderForm = () => {
    let { formDef } = this.props;
    let { fields, groups, enabled } = formDef;

    if (enabled === false) {
      return (
        <View style={styles.emptyText}>
          <Text type="label">
            This form is currently not accepting new submissions. Please check
            back another time.
          </Text>
        </View>
      );
    }

    if (fields.length === 0) {
      return (
        <View style={styles.emptyText}>
          <Text type="label">This form is empty.</Text>
        </View>
      );
    }

    let fieldSets = getFieldSets(groups || EMPTY_GROUPS, fields.toArray());
    let items = fieldSets.map((fieldSet) => {
      let { group, children } = fieldSet;
      if (group == null) {
        return this._renderField(children[0]);
      }
      let isHidden = !this._shouldShowGroup(group);
      // TODO: Use controlled inputs and we won't need to use styling.
      let style: any = { display: isHidden ? 'none' : 'block' };
      return (
        <FieldGroup key={group.id} title={group.title} style={style}>
          {children.map(this._renderField)}
        </FieldGroup>
      );
    });
    return (
      <View style={styles.formWrapper}>
        {items}
        {this._renderPriceRow()}
        {this._renderFormError()}
        {this._renderSubmitButton()}
      </View>
    );
  };

  _renderField = (field: any) => {
    let { markets, addons } = this.props;
    let {
      validationErrors,
      serverValidationErrors,
      formData,
      selectedMarketDates,
    } = this.state;
    if (field.definition.hidden) {
      return null;
    }
    let validationError =
      validationErrors.get(field.id) || serverValidationErrors.get(field.id);
    // Filter add on dates to selected market dates
    return (
      <View key={field.id}>
        {field.renderFormView({
          markets:
            field.type === 'AddonElement'
              ? markets
                  .filter((market) =>
                    Array.isArray(selectedMarketDates?.[market.id]),
                  )
                  .map((market) => {
                    return {
                      ...market,
                      dates: market.dates.filter((date) => {
                        return selectedMarketDates?.[market.id]?.includes(date);
                      }),
                    };
                  })
              : markets,
          addons,
          onChange: this._onFieldChange,
          onValidityChange: this._onFieldValidityChange,
          formData: field.type === 'AddonElement' ? formData : undefined,
        })}
        {this._renderFieldError(validationError)}
      </View>
    );
  };

  _renderPriceRow = () => {
    let { formDef } = this.props;
    if (formDef && formDef.display_price) {
      return (
        <View style={styles.priceText}>
          <Text type="label">Price Estimate: {this._renderPrice()}</Text>
        </View>
      );
    }
  };

  _renderPrice = () => {
    let { markets, priceTiers, addons, formDef } = this.props;
    let { formData, accountCurrency } = this.state;
    const optionFields = getOptionFields(formDef);
    let { totalPrice: price } = calculatePrice(
      markets,
      new IndexedList(formData),
      priceTiers.toArray(),
      addons,
      optionFields,
    );
    if (price < 0) {
      return formatCurrency(0, accountCurrency);
    }
    return formatCurrency(price * 100, accountCurrency);
  };

  _fetchAccountCurrency = async (accountID: string) => {
    let data = await getAccountCurrency(accountID);
    if (data) {
      this.setState({ accountCurrency: data.accountCurrency });
    }
  };

  _fetchAccountAchOnly = async (accountId: string) => {
    const data = await getAccountAchOnly(accountId);
    if (data) {
      this.setState({ achOnly: data.achOnly });
    }
  };

  _renderFormError = () => {
    if (!this.state.formError) {
      return;
    }
    return (
      <Alert
        type="error"
        onDismiss={this._dismissError}
        text={this.state.formError}
        style={styles.alertContainer}
      />
    );
  };

  _renderFieldError = (validationError: any) => {
    if (!validationError) {
      return;
    }
    return <Text style={styles.errorText}>{validationError}</Text>;
  };

  _renderSubmitButton = () => {
    if (this.state.isSubmitting) {
      return <LoadingIndicator />;
    }
    return (
      <View style={styles.centerButton}>
        <Button
          isPrimary
          testID="form-signup-submit"
          title="Submit Application"
          onPress={this._onSubmit}
          style={styles.button}
          hoverStyle={styles.hoveredButton}
          textStyle={styles.buttonText}
        />
      </View>
    );
  };

  _shouldShowGroup = (group: Group) => {
    let { formData } = this.state;
    let { criteria } = group;
    if (!criteria) {
      return true;
    }

    let fieldID = criteria.field_id;
    let fieldData = formData.find((field) => field.id === fieldID);
    if (!fieldData) {
      return true;
    }
    let expectedValue = criteria.field_value;
    let value = fieldData.value;
    return Array.isArray(value)
      ? value.indexOf(expectedValue) !== -1
      : value === expectedValue;
  };

  _setRenewalFormData = (oldData: Array<Field>) => {
    let formData: Array<Field> = [];
    let newDataMap = new Map(
      oldData.map((field) => [
        field.id,
        field.id === APP_FIELDS.ID_FIELD_MARKETS ? {} : field.value,
      ]),
    );

    let { formDef } = this.props;
    for (let field of formDef.fields.toArray()) {
      let id = field.id;
      let fieldDef = field.definition;
      let value = newDataMap.get(id);
      formData.push({
        id,
        name: typeof fieldDef.name === 'string' ? fieldDef.name : '',
        value,
      });
      let element = this.refs[id];
      if (isObject(element) && typeof element.setValue === 'function') {
        if (value != null) {
          element.setValue(value);
        }
      }
    }

    this.setState({ formData });
  };

  _getFormData = () => {
    let { formDef } = this.props;
    let formData: Array<Field> = [];
    formDef.fields.forEach((field) => {
      let fieldDef = field.definition;
      if (fieldDef.hidden) {
        return;
      }
      // Exclude elements that are not interactive (HeadingElement)
      if (!isInteractive(fieldDef.type)) {
        return;
      }
      let element: any = this.refs[field.id];
      if (element) {
        formData.push({
          id: field.id,
          name: typeof fieldDef.name === 'string' ? fieldDef.name : '',
          value: element.getValue(),
        });
      }
    });
    return formData;
  };

  _onFieldChange = (id: string, value: unknown) => {
    let { formData, serverValidationErrors } = this.state;
    let newFormData = formData.map((item) => {
      let { name } = item;
      return item.id === id ? { id, name, value } : item;
    });
    if (id === APP_FIELDS.ID_FIELD_MARKETS) {
      this.setState({
        selectedMarketDates: value as MarketDates,
      });
    }
    this.setState({
      formData: newFormData,
    });
    if (serverValidationErrors.has(id)) {
      let newServerValidationErrors = new Map(serverValidationErrors);
      newServerValidationErrors.delete(id);
      this.setState({
        serverValidationErrors: newServerValidationErrors,
      });
    }
  };

  _onFieldValidityChange = (
    fieldID: string,
    validationError: string | null | undefined,
  ) => {
    let { validationErrors } = this.state;
    let oldValue = validationErrors.get(fieldID);
    let newValue = validationError;
    if (oldValue !== newValue) {
      this.setState({
        validationErrors: new Map(validationErrors).set(fieldID, newValue),
      });
    }
  };

  _onSubmit = () => {
    let { validationErrors } = this.state;
    // TODO: Should we also stop on serverValidationErrors?
    for (let [fieldID, validationError] of validationErrors) {
      if (validationError != null) {
        this._focusErrorField(fieldID);
        return;
      }
    }
    let { accountID, formID } = this.props;
    let results: ObjectOf<unknown> = {};
    for (let { id, value } of this._getFormData()) {
      results[id] = value;
    }
    let data = { results };
    let url = `/public/${accountID}/forms/${formID}`;

    postJSON(url, data, this._formSubmitted);
    this.setState({ isSubmitting: true });
  };

  _formSubmitted = (error: Error | null | undefined, data: ObjectOf<any>) => {
    let { markets, priceTiers, addons, formDef } = this.props;
    let { formData, accountCurrency } = this.state;
    const optionFields = getOptionFields(formDef);
    let { totalPrice: price } = calculatePrice(
      markets,
      new IndexedList(formData),
      priceTiers.toArray(),
      addons,
      optionFields,
    );
    this.setState({ isSubmitting: false });

    if (data && data.error) {
      let { fieldID, description } = data.error;
      this.setState({
        serverValidationErrors: new Map([[fieldID, description]]),
      });
      this._focusErrorField(fieldID);
    } else if (error) {
      this.setState({ formError: error.message });
    } else {
      this.props.onSuccess({
        ...data,
        price: price,
        currency: accountCurrency,
      } as any);
    }
  };

  _focusErrorField = (fieldID: string) => {
    let { formDef } = this.props;
    let fields = formDef.fields;
    let errorField = fields.getByID(fieldID);
    if (errorField) {
      let wrapper = toElement(ReactDOM.findDOMNode(this.refs[errorField.id]));
      if (wrapper) {
        let formEl: any = wrapper.querySelectorAll(
          'input, textarea, select',
        )[0];
        formEl?.focus?.();
      }
    }
  };

  _dismissError = () => {
    this.setState({ formError: null });
  };
}

type SuccessViewState = {
  achOnly: boolean;
};

class SuccessView extends Component<SuccessViewProps, SuccessViewState> {
  state: SuccessViewState = {
    achOnly: false,
  };

  componentDidMount() {
    if (this.props.onViewLoaded) {
      this.props.onViewLoaded();
    }
    this._fetchAccountAchOnly(this.props.accountID);
  }

  render() {
    let {
      content,
      accountID,
      formSubmissionData,
      formDef,
      paymentSettings,
    } = this.props;
    let price = this._getPrice();
    let formattedPrice = formatCurrency(
      this._getPrice(),
      formSubmissionData.currency,
    );
    return (
      <View
        style={[
          styles.successView,
          {
            backgroundColor: formDef?.backgroundColor
              ? getColorString(formDef.backgroundColor)
              : '',
          },
        ]}
      >
        <RawHTMLBlock content={content} />
        {formDef.payment_on_submit ? (
          // TODO: Also, there should be a prop to specify the wording to be
          // displayed on the payment form.
          <PaymentForm
            paymentProvider={formDef.payment_on_submit}
            accountID={accountID}
            vendorID={formSubmissionData.id}
            formattedPrice={formattedPrice}
            totalPriceString={formattedPrice}
            totalPriceNumber={price / 100}
            paymentspring_public_api_key={
              paymentSettings.provider === 'paymentspring'
                ? paymentSettings.publicApiKey
                : ''
            }
            achOnly={this.state.achOnly}
          />
        ) : null}
      </View>
    );
  }

  // This will return the price x100
  _getPrice = () => {
    let validate = (price: number) => (isNaN(price) || price < 0 ? 0 : price);
    let { formDef, formSubmissionData } = this.props;
    switch (formDef.feeOption) {
      case 'fixed':
        return validate(formDef.feeValue);
      case 'percentage':
        return validate(formDef.feeValue * Number(formSubmissionData.price));
      default:
        return validate(Number(formSubmissionData.price));
    }
  };

  _fetchAccountAchOnly = async (accountId: string) => {
    const data = await getAccountAchOnly(accountId);
    if (data) {
      this.setState({ achOnly: data.achOnly });
    }
  };
}

class FormEmbed extends Component<FormEmbedFullProps, FormEmbedState> {
  // TODO: this state situation would be a great place to use a tagged union
  state: FormEmbedState = {
    isPreambleAccepted: false,
    isRenewalDismissed: false,
    formSubmitSuccess: null,
    renewalData: null,
  };

  render() {
    let {
      formDef: formDefNullable,
      markets,
      priceTiers,
      paymentSettings,
      ...otherProps
    } = this.props;
    let {
      isPreambleAccepted,
      isRenewalDismissed,
      formSubmitSuccess,
      renewalData,
    } = this.state;
    if (
      formDefNullable == null ||
      markets == null ||
      priceTiers == null ||
      paymentSettings == null
    ) {
      return <LoadingIndicator />;
    }
    let formDef = formDefNullable as SignupFormModel;
    if (formDef.show_preamble && !isPreambleAccepted) {
      return (
        <PreambleView
          {...otherProps}
          content={formDef.preamble_content}
          onAccept={this._onAccept}
        />
      );
    }
    if (formDef.allow_renewal && !isRenewalDismissed) {
      return (
        <RenewalView
          {...otherProps}
          onClose={this._onDismissRenewal}
          onRenewalDataReceived={(renewalData) => {
            this.setState({ isRenewalDismissed: true, renewalData });
          }}
        />
      );
    }
    if (formSubmitSuccess) {
      return (
        <SuccessView
          formDef={formDef}
          paymentSettings={paymentSettings}
          {...otherProps}
          content={formDef.post_submit_content}
          formSubmissionData={formSubmitSuccess as any}
        />
      );
    }
    return (
      <FormView
        formDef={formDef}
        markets={markets}
        priceTiers={priceTiers}
        {...otherProps}
        renewalData={renewalData}
        onSuccess={this._onFormSubmitSuccess}
      />
    );
  }

  _onAccept = () => {
    this.setState({ isPreambleAccepted: true });
  };

  _onDismissRenewal = () => {
    this.setState({ isRenewalDismissed: true });
  };

  _onFormSubmitSuccess = (data: FormSuccessProps) => {
    this.setState({ formSubmitSuccess: data });
  };
}

let styles = StyleSheet.create({
  form: {
    borderWidth: 1,
    borderColor: themeColors.borderGrey,
  },
  formWrapper: {
    maxWidth: 960,
  },
  alertContainer: {
    marginHorizontal: 30,
  },
  emptyText: {
    paddingHorizontal: 30,
    paddingVertical: 10,
  },
  priceText: {
    paddingHorizontal: 30,
    marginBottom: 5,
  },
  errorText: {
    paddingHorizontal: 30,
    marginBottom: 5,
    color: themeColors.red,
    fontSize: 12,
  },
  centerButton: {
    alignItems: 'center',
    marginVertical: 10,
  },
  button: {
    marginHorizontal: 20,
    backgroundColor: '#337ab7',
    borderColor: '#2e6da4',
    borderWidth: 1,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  buttonText: {
    fontSize: 18,
    fontWeight: '400',
    color: themeColors.white,
    paddingVertical: 10,
    paddingHorizontal: 15,
  },
  hoveredButton: {
    backgroundColor: '#286090',
    borderColor: '#204d74',
    borderWidth: 1,
  },
  successView: {
    borderWidth: 1,
    borderColor: themeColors.borderGrey,
    paddingHorizontal: 30,
    paddingVertical: 10,
  },
});

const connector = connectData<FormEmbedProps>()(
  (props: { accountID: string; formID: string }) => ({
    formDef: {
      dataStoreKey: 'forms/:id',
      params: [props.formID],
    },
    markets: {
      dataStoreKey: 'markets',
    },
    addons: {
      dataStoreKey: 'addons',
    },
    priceTiers: {
      dataStoreKey: 'priceTiers',
    },
    paymentSettings: {
      dataStoreKey: 'paymentSettings',
    },
  }),
);
type FormEmbedFullProps = ConnectedProps<typeof connector>;
export default connector(FormEmbed);
